import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  type BasicPreBookingFragment,
  type BasicPresenceFragment,
  GetBusinessHoursDocument,
  GetPreBookingsDocument,
  GetPresencesDocument,
} from '@drycut/graphql';
import { CircleButton, Heading, Stack } from '@drycut/ui-components';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { useCheckoutStore } from '../../checkoutStore';
import DaySelect from './DaySelect';
import SlotSelect from './SlotSelect';
import { getDaySchedule } from './utils';

export type WeekInfo = {
  endOfWeek: DateTime;
  startOfWeek: DateTime;
  weekMonth: string;
  weekNumber: number;
  year: number;
};

type Props = {
  onSlotCancel: () => void;
  onSlotSelect: (time: DateTime) => void;
};

export function ScheduleSelect({ onSlotSelect }: Props): React.ReactElement {
  const selectedStore = useCheckoutStore((state) => state.selectedStore);
  const selectedSchedule = useCheckoutStore((state) => state.selectedSchedule);
  const [selectedDate, setSelectedDate] = useState<DateTime>(selectedSchedule || DateTime.now());

  // The queries to get the relevant data for the selected store
  const [getBusinessHours, { loading: businessHoursLoading, data: businessHoursData }] =
    useLazyQuery(GetBusinessHoursDocument, { fetchPolicy: 'network-only' });
  const [getPreBookings, { loading: prebookingsLoading, data: preBookingsData }] = useLazyQuery(
    GetPreBookingsDocument,
    { fetchPolicy: 'network-only' },
  );

  const [weekInfo, setWeekInfo] = useState<WeekInfo>({
    weekNumber: DateTime.now().weekNumber,
    year: DateTime.now().year,
    weekMonth: DateTime.now().toFormat('MMMM'),
    startOfWeek: DateTime.now().startOf('week'),
    endOfWeek: DateTime.now().endOf('week'),
  });

  const { loading: presencesLoading, data: presencesData } = useQuery(GetPresencesDocument, {
    variables: {
      storeId: selectedStore?.id || '',
      from: weekInfo.startOfWeek.toISODate() ?? '',
      to: weekInfo.endOfWeek.toISODate() ?? '',
    },
    skip: !selectedStore,
  });

  const preBookings = preBookingsData?.preBookings?.nodes || [];
  const validPreBookings = preBookings.filter((node): node is BasicPreBookingFragment =>
    Boolean(node),
  );
  const presences = presencesData?.presences?.nodes || [];
  const validPresences = presences.filter((node): node is BasicPresenceFragment => Boolean(node));

  const businessHours = businessHoursData?.businessHours;

  // Change the default selected date to the first available date in the next 5 days
  useEffect(() => {
    if (!businessHours) return;

    // Generate array of next 5 days and find first available
    const availableDate = Array.from({ length: 6 }, (_, i) => selectedDate.plus({ days: i })).find(
      (date) => !getDaySchedule(date, businessHours).closed,
    );

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (availableDate && availableDate.toISODate() !== selectedDate.toISODate()) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-hooks/set-state-in-effect
      setSelectedDate(availableDate);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setWeekInfo({
        weekNumber: availableDate.weekNumber,
        year: availableDate.year,
        weekMonth: availableDate.toFormat('MMMM'),
        startOfWeek: availableDate.startOf('week'),
        endOfWeek: availableDate.endOf('week'),
      });
    }
  }, [businessHours, selectedDate]);

  // Fetch the data when the component mounts. We need to refetch the data every time so we are
  // sure we're getting the most up-to-date information.
  useEffect(() => {
    if (selectedStore) {
      getBusinessHours({ variables: { storeId: selectedStore.id } });
      getPreBookings({ variables: { storeId: selectedStore.id } });
    }
  }, [selectedStore, getBusinessHours, getPreBookings]);

  const loading = presencesLoading || businessHoursLoading || prebookingsLoading;

  const previousEnabled = !loading && weekInfo.weekNumber !== DateTime.now().weekNumber;

  // Can only navigate to the next week if the current day + 5 days is in the next week.
  const nextEnabled = !loading && DateTime.now().plus({ days: 5 }).weekNumber > weekInfo.weekNumber;

  const handleNextWeek = () => {
    const nextWeekDate = weekInfo.startOfWeek.plus({ weeks: 1 });

    setWeekInfo({
      weekNumber: nextWeekDate.weekNumber,
      year: nextWeekDate.year,
      weekMonth: nextWeekDate.toFormat('MMMM'),
      startOfWeek: nextWeekDate.startOf('week'),
      endOfWeek: nextWeekDate.endOf('week'),
    });
  };

  const handlePreviousWeek = () => {
    const prevWeekDate = weekInfo.startOfWeek.minus({ weeks: 1 });

    setWeekInfo({
      weekNumber: prevWeekDate.weekNumber,
      year: prevWeekDate.year,
      weekMonth: prevWeekDate.toFormat('MMMM'),
      startOfWeek: prevWeekDate.startOf('week'),
      endOfWeek: prevWeekDate.endOf('week'),
    });
  };

  // Final selection, after selecting then time slot, that bubbles up the selection to the context
  const handleSlotSelect = (time: DateTime) => {
    setSelectedDate(time);
    onSlotSelect(time);
  };

  return (
    <Stack gap={6}>
      <Stack gap={3}>
        <Stack direction="row" gap={2} className="justify-between">
          <CircleButton onClick={handlePreviousWeek} disabled={!previousEnabled}>
            <ArrowLeftIcon className="size-6" />
          </CircleButton>

          <Heading variant="h4" className="font-ramillas">
            {weekInfo.startOfWeek.setLocale('de-CH').toFormat('MMMM')}
          </Heading>

          <CircleButton onClick={handleNextWeek} disabled={!nextEnabled}>
            <ArrowRightIcon className="size-6" />
          </CircleButton>
        </Stack>

        {businessHours ? (
          <DaySelect
            weekInfo={weekInfo}
            businessHours={businessHours}
            selectedDate={selectedDate}
            onSelect={setSelectedDate}
          />
        ) : (
          <>Loading</>
        )}
      </Stack>

      <div className="h-px bg-gray-800/5" />

      <Heading variant="h5" className="font-ramillas">
        {selectedDate.setLocale('de-CH').toFormat('EEEE, d. MMMM')}
      </Heading>

      {businessHours ? (
        <SlotSelect
          selectedDate={selectedDate}
          onSelect={handleSlotSelect}
          preBookings={validPreBookings}
          presences={validPresences}
        />
      ) : (
        <>Loading...</>
      )}
    </Stack>
  );
}
